import { useQueries } from '@tanstack/react-query';
import type { AgentTask } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { TaskDisplay } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { queryKeys } from './query-client';
import { useWorkspaces } from './workspaces';
import { fetchWorkspaceTasks } from './workspace-tasks';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import { buildIslandQuickView, islandLead, type IslandLead, type IslandQuickView, type QuickViewTask } from './island-quickview';
import type { IslandColor } from './agent-task-buckets';

export type IslandQuickViewState = { status: 'loading' } | { status: 'ready'; data: IslandQuickView } | { status: 'error' };

/**
 * The island quick-view's data — the grouped, waiting-first task list the popover (web) / sheet (native)
 * render. Reads the SAME per-workspace task cache the Your Agents strip and the workspace screens use
 * (`queryKeys.workspaceTasks`), so opening it is a cache hit, and refetches live on agent-task changes.
 * The AgentTask → row mapping lives here; the ordering/grouping is the shared pure builder.
 */
export function useIslandQuickView(): IslandQuickViewState {
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];

  const queries = useQueries({
    queries: workspaces.map((workspace) => ({
      queryKey: queryKeys.workspaceTasks(workspace.id),
      queryFn: () => fetchWorkspaceTasks(workspace.id),
    })),
  });

  useAgentTaskDuplexer(() => {
    for (const query of queries) void query.refetch();
  });

  if (workspacesState.status === 'error') return { status: 'error' };
  if (workspacesState.status === 'loading') return { status: 'loading' };

  // Stay in `loading` until every workspace's tasks have landed at least once — otherwise the bubble opens
  // with an empty list (just the footer) and pops the rows in a beat later. Once cached, re-opens are instant.
  const stillLoading = queries.some((q) => q.isLoading && q.data === undefined);
  const anyData = queries.some((q) => q.data !== undefined);
  if (stillLoading && !anyData) return { status: 'loading' };

  const tasks: QuickViewTask[] = [];
  workspaces.forEach((workspace, i) => {
    for (const task of (queries[i]?.data ?? []) as AgentTask[]) {
      tasks.push({
        id: task.id ?? '',
        title: (task.title || (task.display as TaskDisplay | undefined)?.title || '').trim(),
        status: task.status ?? '',
        agentId: task.agentId,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        summary: ((task.display as TaskDisplay | undefined)?.summary || '').trim() || undefined,
        updatedDate: task.updatedDate,
      });
    }
  });

  return { status: 'ready', data: buildIslandQuickView(tasks) };
}

/**
 * The pill's lead task for its current color — the line-2 source of the expanded blue/orange capsule.
 * Reads the SAME task union `useIslandQuickView` builds (one shared React Query cache, so no extra
 * fetch), and picks the newest task in the color's bucket from the FULL sorted set. `null` while the
 * union is still loading, for green, or when the color's bucket is empty — the pill then stays one line.
 */
export function useIslandLead(color: IslandColor): IslandLead | null {
  const state = useIslandQuickView();
  if (state.status !== 'ready') return null;
  return islandLead(state.data.sorted, color);
}
